import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Check, Email, Warning } from '@material-ui/icons';
import { Grid, InputAdornment } from '@material-ui/core';
import {
  AddressInput,
  IconButton,
  Panel,
  PhoneInput,
  PreventTransitionPrompt,
  Switch,
  TextField,
  TextFieldUOM,
  useDialogs,
  isPossiblePhoneNumber,
  LoadingBounce,
  Button,
  Select,
  InformationTooltip,
} from '@michelin/acid-components';
import { UOM } from '@michelin/acid-components/build/components/TextFieldUOM/TextFieldUOM';
import useUtils from 'hooks/useUtils';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import { useSaveGeoCommercialLocation, getGeoLocation } from 'hooks/useGeoLocation';
import { getOptions } from '@michelin/select-options-provider';
import { useLocationST } from 'hooks/useLocation';
import { useSnackbar } from 'notistack';
import { GenericActionButtons } from 'components/GenericActionButtons/GenericActionButtons';
import { scrollToDisplayFirstError } from 'Utils';
import POSInfoIcon from 'components/POS/POSInfoIcon';
import { checkCommercialMaxServiceRadius, checkCommercialMinServiceRadius } from 'components/Location/LocationPanel';
import POS from '../POS/POS';
import { useSaveCommercialLocation } from '../../hooks/useCommercialLocation';
import GooglePlaces, { OnChangeIds } from '../GooglePlaces';
import {
  GooglePlacesAddress,
  GooglePlacesInfo,
  GooglePlacesLatLng,
  GooglePlacesMapInfo,
} from '../../Types/GooglePlaces';
import {
  Customer,
  GlobalTextInputProps,
  HoursOfOperation as HoursOfOperationType,
  POSDetails,
} from '../../Types';
import HoursOfOperations from '../HoursOfOperation/Index';
import { Address, CommercialLocation } from './Fields';

interface Props {
  commercial: CommercialLocation,
  customer: Customer,
  action: 'edit' | 'view' | 'add',
}

function CommercialLocationPanel(props: Props) {
  const history = useHistory();
  const permissions = usePermissionsSPP();
  const { t } = useTranslation();
  const { stNumber, btNumber } = useParams<{ stNumber: string, btNumber: string }>();
  const locationQuery = useLocationST(stNumber);
  const { enqueueSnackbar } = useSnackbar();
  const { emailRegex, urlRegex, getValuesByKey } = useUtils();
  const { errorDialog } = useDialogs();
  const [googlePlacesInfo, setGooglePlacesInfo] = useState<GooglePlacesInfo | null>(null);
  const [googlePlacesStaus, setGooglePlacesStatus] = useState<'loading' | 'error' | 'success' | 'invalid' | 'none'>('none');

  const mutateCommercial = useSaveCommercialLocation(stNumber);
  const mutateGeoCommercial = useSaveGeoCommercialLocation(stNumber);
  const {
    formState,
    getValues,
    setValue,
    control,
    watch,
    reset,
    handleSubmit,
  } = useForm<CommercialLocation>({ defaultValues: props.commercial });

  const { customer } = props;
  const readOnly: boolean = props.action === 'view';
  const country: string = customer?.customer_country?.toLocaleLowerCase() || 'us';
  const [refresh, setRefresh] = useState(0);
  const lastAddressLoaded = useRef<string>(JSON.stringify({
    ...props.commercial.service_address,
    country: (country === 'us' ? 'USA' : 'CAN'),
  }));

  const updateGeoLocation = async (address: any) => {
    if (!address.state) return;
    const jsonAddress = JSON.stringify(address);
    if (lastAddressLoaded.current === jsonAddress) return;
    lastAddressLoaded.current = jsonAddress;
    const states = getOptions((country === 'us' ? 'us_states' : 'ca_provinces'));
    const state = states.filter((i) => i.value === address.state)[0].label;
    const cloneAddress: GooglePlacesAddress = {
      address: address.address1,
      city: address.city,
      state,
      zip: address.zip,
      country: (country === 'us' ? 'USA' : 'CAN'),
    };

    try {
      setGooglePlacesStatus('loading');
      const res: GooglePlacesLatLng = await getGeoLocation(cloneAddress);
      if (!res) throw new Error('No results found');
      if (res.location_type !== 'ROOFTOP') {
        setGooglePlacesStatus('invalid');
        return;
      }

      const cloneGooglePlaces = cloneDeep(getValues().google_places) || {} as GooglePlacesMapInfo;
      cloneGooglePlaces.geo_code_latitude = res.lat.toString();
      cloneGooglePlaces.geo_code_longitude = res.lng.toString();

      setRefresh(refresh + 1);
      setValue('google_places', cloneGooglePlaces, { shouldDirty: true });
      setGooglePlacesStatus('success');
    } catch (e) {
      setGooglePlacesStatus('error');
    }
  };

  useEffect(() => {
    if (props.action === 'view') reset();
  }, [props.action, reset]);

  useEffect(() => {
    reset(props.commercial);
  }, [props.commercial, reset]);

  if (!locationQuery.data) return <LoadingBounce style={{ height: '70vh' }} />;
  const sameAsPhisicalAddressReadOnlyForKAD = locationQuery.data.kad_location
    && !locationQuery.data.is_oliver_dealer
    && !locationQuery.data.tad_location
    && !locationQuery.data.commercial_truck_location;

  const inputProps: GlobalTextInputProps = {
    variant: 'standard',
    InputProps: { readOnly },
    style: { display: 'flex', flexWrap: 'wrap' },
  };

  const onError = (errors: any) => {
    const errorMessages: string[] = getValuesByKey(errors, 'message');
    const message = (errorMessages.filter((m) => m !== '').length > 0 ? errorMessages : t('Please check the marked fields.').split('\n'));

    errorDialog(
      message,
      t('Error saving commercial data.'),
    );
    scrollToDisplayFirstError();
  };

  const save = async (data: CommercialLocation) => {
    try {
      if (getValues('headquarters_flag') && getValues('has_pos_system') && !getValues('pos_details.pos_provider')) {
        onError({});
        return;
      }

      if (getValues('headquarters_flag') && getValues('has_pos_system') && getValues('pos_details.pos_provider') === 'OTHER' && !getValues('pos_details.other_name')) {
        onError({});
        return;
      }

      await mutateCommercial.mutateAsync({ stNumber, data });
      reset();
      history.push('view');
      enqueueSnackbar(t('Commercial Location information saved.'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
    } catch (e) {
      errorDialog(
        [
          ...(e.toString()
            ? [`${t(e.toString())}`]
            : ('Please check your connection and try again.\nIf the issue persist, please contact support').split('\n')),
        ],
        t('Error saving Location Data.'),
      );
    }
  };

  const copyHoo = () => {
    const newHoo = { ...getValues().tire_services_hours_of_operation };
    setValue('mechanical_hours_of_operation', newHoo || {} as HoursOfOperationType, { shouldDirty: true });
  };

  const copyHooFromTire = () => {
    setValue('mechanical_hours_of_operation', {} as HoursOfOperationType);
    copyHoo();
  };

  const copyPhysicalAddress = (address: Address) => {
    setValue('service_address', address, { shouldDirty: true });
  };

  const renderPhysicalAddresInformation = () => {
    if (!customer) return null;

    return (
      <Panel title={t('Physical Address Information')}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              id="customer_name"
              label={t('Legal Ship To Name')}
              variant={inputProps.variant}
              style={inputProps.style}
              value={customer?.customer_name || ''}
              readOnly
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              id="dba_name"
              label={t('DBA Name')}
              variant={inputProps.variant}
              style={inputProps.style}
              value={customer?.customer_dba_name || ''}
              readOnly
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <AddressInput
              stacked
              showAddress2
              showCountry
              onChange={() => { }}
              readOnly
              address={{
                address1: customer?.customer_addr1,
                address2: customer?.customer_addr2,
                city: customer?.customer_city,
                state: customer?.customer_state,
                zip: customer?.customer_zip,
                country: (country === 'us' ? 'USA' : 'CAN'),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              id="county"
              label={t('County')}
              variant={inputProps.variant}
              style={inputProps.style}
              value={customer?.customer_county || ''}
              readOnly
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="inter_hwy_info"
              control={control}
              render={(item) => (
                <TextField
                  label={t('Interstate/Hwy Exit Information')}
                  readOnly={readOnly}
                  variant={inputProps.variant}
                  style={inputProps.style}
                  onChange={(event) => { item.field.onChange(event.target.value); }}
                  value={item.field.value}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="is_mobile_only"
              control={control}
              render={(item) => (
                <Switch
                  id={item.field.name}
                  label={t('Mobile Only')}
                  labelPosition="left"
                  readOnly={readOnly}
                  checked={item.field.value === true}
                  color="primary"
                  uncheckedLabel={t('No')}
                  checkedLabel={t('Yes')}
                  onChange={(event, newValue) => item.field.onChange(newValue)}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="open_for_commercial_business"
              control={control}
              render={(item) => (
                <Switch
                  id={item.field.name}
                  label={t('Open for Business')}
                  labelPosition="left"
                  readOnly={readOnly}
                  checked={item.field.value === true}
                  color="primary"
                  uncheckedLabel={t('No')}
                  checkedLabel={t('Yes')}
                  onChange={(event, newValue) => item.field.onChange(newValue)}
                />
              )}
            />
          </Grid>
        </Grid>
      </Panel>
    );
  };

  const renderServiceAddressInformation = () => {
    if (!customer) return null;

    return (
      <Panel title={t('Service Address Information')}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <Controller
                  name="service_radius"
                  control={control}
                  render={(item) => (
                    <TextFieldUOM
                      id="service_radius"
                      variant={inputProps.variant || 'standard'}
                      style={{ display: 'flex', flexWrap: 'wrap' }}
                      uom={country === 'us' ? UOM.miles : UOM.km}
                      onChange={(newValue: string) => item.field.onChange(checkCommercialMaxServiceRadius(newValue, country))}
                      onBlur={() => item.field.onChange(checkCommercialMinServiceRadius(item.field.value, country))}
                      value={checkCommercialMaxServiceRadius(item.field.value?.toString() || '', country) || ''}
                      label={t('Service Radius')}
                      decimals={2}
                      min={0}
                      step={1}
                      readOnly={readOnly}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <InformationTooltip
                              tooltipVariant="light"
                              title={t(
                                country === 'us'
                                  ? 'Entered values must be between 30 and 50 Miles. If blank, service radius will follow the ONCall default.'
                                  : 'Entered values must be between 49 and 80 KM. If blank, service radius will follow the ONCall default.',
                              )}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  disabled={readOnly}
                  onClick={() => {
                    const newAddress: Address = {
                      address1: customer.customer_addr1,
                      city: customer.customer_city,
                      state: customer.customer_state,
                      zip: customer.customer_zip,
                    };
                    copyPhysicalAddress(newAddress);
                    updateGeoLocation(newAddress);
                  }}
                >
                  {t('Copy from Physical Address')}
                </Button>
              </Grid>
              <Grid item xs={12} md={12}>
                <Controller
                  name="service_address"
                  control={control}
                  rules={{
                    validate: (address: Address) => {
                      const poBox = ['po box', 'p.o. box', 'pobox', 'pobx', 'po bx'];
                      if (address.address1 === '' || address.city === '' || address.state === '' || address.zip === '') return 'The address can\'t be empty.';
                      if (poBox.some((word) => (address.address1 || '').toLowerCase().includes(word))) return 'The address can\'t be a PO Box';
                      return true;
                    },
                  }}
                  render={(item) => (
                    <AddressInput
                      stacked
                      readOnly={readOnly || sameAsPhisicalAddressReadOnlyForKAD}
                      address={{
                        ...item.field.value,
                        country: (country === 'us' ? 'USA' : 'CAN'),
                      }}
                      error={!!item.fieldState.error}
                      onChange={(field, fieldValue, value) => {
                        item.field.onChange({
                          ...value,
                          country: (country === 'us' ? 'USA' : 'CAN'),
                        });
                      }}
                      onBlur={(field, fieldValue, value) => updateGeoLocation(value)}
                    />
                  )}
                />
              </Grid>
              {googlePlacesStaus === 'loading' && (
                <Grid item xs={12}>
                  <div style={{ float: 'left', width: 70, zoom: 0.6 }}>
                    <LoadingBounce />
                  </div>
                  <div style={{
                    float: 'left', marginLeft: 4, paddingTop: 2, fontSize: '0.8em',
                  }}
                  >
                    {t('Updating GPS location from Google Maps...')}
                  </div>
                </Grid>
              )}
              {googlePlacesStaus === 'error' && (
                <Grid item xs={12}>
                  <Warning style={{ height: 20, verticalAlign: 'bottom' }} color="error" />
                  <span style={{ fontSize: '0.8em' }}>
                    {t('Error loading GPS Coordinates from Google Maps, please try again or use the map to set GPS location manually.')}
                  </span>
                </Grid>
              )}
              {googlePlacesStaus === 'success' && (
                <Grid item xs={12}>
                  <Check style={{ height: 20, verticalAlign: 'bottom' }} color="primary" />
                  <span style={{ fontSize: '0.8em' }}>
                    {t('GPS Coordinates has been updated using Google Maps.')}
                  </span>
                </Grid>
              )}
              {googlePlacesStaus === 'invalid' && (
                <Grid item xs={12}>
                  <Warning style={{ height: 20, verticalAlign: 'bottom', color: 'rgb(249, 198, 55)' }} />
                  <span style={{ fontSize: '0.8em' }}>
                    {t('Google Maps returned invalid GPS Coordinates. Please check the address or use the map to set GPS location manually.')}
                  </span>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <Controller
                  name="google_places"
                  control={control}
                  render={(item) => (
                    <GooglePlaces
                      customer_name={customer.customer_name || ''}
                      googlePlacesMapInfo={item.field.value || {} as GooglePlacesMapInfo}
                      onChange={(fieldId: OnChangeIds, fieldValue: any, value: GooglePlacesMapInfo) => {
                        item.field.onChange(value);
                      }}
                      googlePlacesInfo={googlePlacesInfo || undefined}
                      googlePlacesInfoOnChange={(newGooglePlacesInfo: GooglePlacesInfo) => {
                        setGooglePlacesInfo(newGooglePlacesInfo);
                      }}
                      inputProps={inputProps}
                      acceptLocationShow={permissions.allowsAction('location_information.modify_lat_long_coordinates')}
                      acceptLocationOnSave={async (geoData: any) => {
                        try {
                          if (!readOnly) {
                            const cloneGooglePlaces = getValues().google_places || {} as GooglePlacesMapInfo;

                            cloneGooglePlaces.geo_code_latitude = geoData.geo.geo_code_latitude;
                            cloneGooglePlaces.geo_code_longitude = geoData.geo.geo_code_longitude;
                            cloneGooglePlaces.requested_geo_code_explanation = '';
                            cloneGooglePlaces.requested_geo_code_update = false;
                            cloneGooglePlaces.requested_geo_code_latitude = undefined;
                            cloneGooglePlaces.requested_geo_code_longitude = undefined;

                            setValue('google_places', cloneGooglePlaces);
                            setValue('requested_geo_code_explanation', '');
                            setValue('requested_geo_code_update', false);
                            setValue('requested_geo_code_latitude', undefined);
                            setValue('requested_geo_code_longitude', undefined);

                            save(getValues());
                          } else {
                            await mutateGeoCommercial.mutateAsync({ stNumber, data: geoData });
                            reset();
                            history.push('view');
                          }
                        } catch (e) {
                          errorDialog(
                            t('Please check your connection and try again.\nIf the issue persist, please contact support').split('\n'),
                            t('Error saving Location Data.'),
                          );
                        }
                      }}
                      readOnly={readOnly}
                      errors={{} as any}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Panel>
    );
  };

  const renderMailingAddressInformation = () => {
    if (!customer) return null;

    return (
      <Panel title={t('Mailing Address Information')}>
        <Controller
          name="mailing_address"
          control={control}
          render={(item) => (
            <AddressInput
              stacked
              address={{ ...item.field.value, country: country === 'us' ? 'USA' : 'CAN' }}
              showAddress2
              showCountry
              onChange={(field, fieldValue, value) => item.field.onChange(value)}
              readOnly
            />
          )}
        />

      </Panel>
    );
  };

  const renderMichelinIntegrations = () => {
    if (!customer) return null;

    return (
      <Panel title={t('Michelin Integrations')}>
        <POSInfoIcon />
        <Controller
          name="pos_type"
          control={control}
          render={(item) => (
            <Select
              id={item.field.name}
              name="pos_type"
              data-testid="pos_type-test"
              label={t('POS Integration')}
              value={item.field.value}
              style={{ width: '100%' }}
              readOnly={readOnly || !permissions.allowsAction('michelin_integrations.update_pos_integration')}
              required
              options={getOptions('pos_type')}
              onChange={(e) => item.field.onChange(e.target.value)}
              error={!!formState.errors.pos_type}
            />
          )}
        />

      </Panel>
    );
  };

  const renderContactInformation = () => {
    if (!customer) return null;
    return (
      <Panel title={t('Contact Information')}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Controller
              name="st_phone"
              control={control}
              rules={{ validate: (value: string) => isPossiblePhoneNumber(value) || value === '' }}
              render={(item) => (
                <PhoneInput
                  id="st_phone"
                  label={t('Phone Number')}
                  value={item.field.value}
                  defaultCountry={(country === 'ca' ? 'CA' : 'US')}
                  readOnly={readOnly}
                  required={false}
                  onChange={(e) => {
                    item.field.onChange(e.value || '');
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Controller
              name="toll_free_number"
              control={control}
              rules={{ validate: (value: string) => isPossiblePhoneNumber(value) || value === '' }}
              render={(item) => (
                <PhoneInput
                  id="toll_free_number"
                  label={t('Toll Free Phone Number')}
                  value={item.field.value || ''}
                  defaultCountry={(country === 'ca' ? 'CA' : 'US')}
                  readOnly={readOnly}
                  required={false}
                  onChange={(e) => {
                    item.field.onChange(e.value || '');
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Controller
              name="after_hours_phone_number"
              control={control}
              rules={{ required: true, validate: isPossiblePhoneNumber }}
              render={(item) => (
                <PhoneInput
                  id="after_hours_phone_number"
                  label={t('After Hours Phone Number')}
                  value={item.field.value || ''}
                  defaultCountry={(country === 'ca' ? 'CA' : 'US')}
                  readOnly={readOnly}
                  required
                  onChange={(e) => {
                    item.field.onChange(e.value || '');
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Controller
              name="secondary_after_hours_phone_number"
              control={control}
              rules={{ validate: (value: string) => isPossiblePhoneNumber(value) || value === '' }}
              render={(item) => (
                <PhoneInput
                  id="secondary_after_hours_phone_number"
                  label={t('Secondary After Hours Phone Number')}
                  value={item.field.value || ''}
                  defaultCountry={(country === 'ca' ? 'CA' : 'US')}
                  readOnly={readOnly}
                  required={false}
                  onChange={(e) => {
                    item.field.onChange(e.value || '');
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Grid container alignItems="flex-end">
              <Controller
                name="service_request_email"
                control={control}
                rules={{ pattern: emailRegex }}
                render={(item) => (
                  <TextField
                    label={t('Service Request Email')}
                    value={item.field.value || ''}
                    variant={inputProps.variant}
                    style={inputProps.style}
                    onChange={(e) => item.field.onChange(e.target.value)}
                    error={!!item.formState.errors.service_request_email}
                    InputProps={{
                      readOnly,
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            aria-label={t('Service Request Email')}
                            disabled
                          >
                            <Email />
                          </IconButton>
                        </InputAdornment>),
                    }}
                  />
                )}
              />

            </Grid>
          </Grid>

          <Grid item xs={12} md={12}>
            <Grid container alignItems="flex-end">
              <Controller
                name="store_email"
                control={control}
                rules={{ pattern: emailRegex }}
                render={(item) => (
                  <TextField
                    label={t('Store Email')}
                    value={item.field.value || ''}
                    variant={inputProps.variant}
                    style={inputProps.style}
                    onChange={(e) => item.field.onChange(e.target.value)}
                    error={!!item.formState.errors.store_email}
                    InputProps={{
                      readOnly,
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            aria-label={t('Store Email')}
                            disabled
                          >
                            <Email />
                          </IconButton>
                        </InputAdornment>),
                    }}
                  />
                )}
              />

            </Grid>
          </Grid>
          <Grid item xs={12} md={12}>
            <Controller
              name="site_url"
              control={control}
              rules={{ pattern: urlRegex }}
              render={(item) => (
                <TextField
                  label={t('Website Address')}
                  value={item.field.value || ''}
                  variant={inputProps.variant}
                  style={inputProps.style}
                  readOnly={readOnly}
                  onChange={(e) => item.field.onChange(e.target.value)}
                  error={!!item.formState.errors.site_url}
                />
              )}
            />
          </Grid>
        </Grid>
      </Panel>

    );
  };

  const renderHoursOfOperations = () => {
    const sameAsTire = watch('mechanical_same_hours_as_tires') || false;
    const google_places_id = watch('google_places_id') || null;

    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <Panel title={t('Tire Services Hours of Operations')}>
            <Grid item xs={12} md={12} style={{ paddingTop: '38px' }}>
              <Controller
                name="tire_services_hours_of_operation"
                control={control}
                render={(item) => (
                  <HoursOfOperations
                    googlePlacesId={google_places_id || ''}
                    googlePlacesInfo={googlePlacesInfo || undefined}
                    googlePlacesInfoOnChange={(newGooglePlacesInfo: GooglePlacesInfo) => {
                      setGooglePlacesInfo(newGooglePlacesInfo);
                    }}
                    onChange={(newHoo: HoursOfOperationType) => {
                      item.field.onChange(newHoo);
                      if (sameAsTire) copyHoo();
                    }}
                    hoursOfOperation={item.field.value || {} as HoursOfOperationType}
                    readOnly={readOnly}
                    country={country}
                  />
                )}
              />
            </Grid>
          </Panel>
        </Grid>
        <Grid item xs={12} md={6}>
          <Panel title={t('Mechanical Services Hours of Operations')}>
            <Controller
              name="mechanical_same_hours_as_tires"
              control={control}
              render={(item) => (
                <Switch
                  id={item.field.name}
                  labelPosition="left"
                  label={t('Mechanical same as Tires')}
                  readOnly={readOnly}
                  color="primary"
                  uncheckedLabel={t('No')}
                  checkedLabel={t('Yes')}
                  checked={item.field.value === true}
                  onChange={(event, newValue) => {
                    if (newValue) copyHoo();
                    else { setValue('mechanical_hours_of_operation', {} as HoursOfOperationType, { shouldDirty: true }); }
                    item.field.onChange(newValue);
                  }}

                />
              )}
            />
            <Controller
              name="mechanical_hours_of_operation"
              control={control}
              render={(item) => (
                <HoursOfOperations
                  googlePlacesId={google_places_id || ''}
                  googlePlacesInfo={googlePlacesInfo || undefined}
                  googlePlacesInfoOnChange={(newGooglePlacesInfo: GooglePlacesInfo) => {
                    setGooglePlacesInfo(newGooglePlacesInfo);
                  }}
                  onChange={(newHoo: HoursOfOperationType) => {
                    item.field.onChange(newHoo);
                  }}
                  copyWorkHoursFrom={copyHooFromTire}
                  hoursOfOperation={item.field.value || {} as HoursOfOperationType}
                  readOnly={readOnly}
                  country={country}
                  copyMode={sameAsTire}
                />

              )}
            />
          </Panel>
        </Grid>
      </Grid>
    );
  };

  const renderPosDetails = () => {
    if (!getValues().headquarters_flag) return null;
    const hasPosSystem = watch('has_pos_system');
    const headquartersFlag = watch('headquarters_flag');

    if (!headquartersFlag) return null;

    return (
      <Grid item xs={12} md={12}>
        <Panel title={t('POS')}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="has_pos_system"
                control={control}
                render={(item) => (
                  <Switch
                    labelPosition="left"
                    label={t('Do you have a POS System?')}
                    uncheckedLabel={t('No')}
                    checkedLabel={t('Yes')}
                    readOnly={readOnly}
                    checked={item.field.value === true}
                    onChange={(event, newValue) => {
                      item.field.onChange(newValue);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              {(hasPosSystem ? (
                <Controller
                  name="pos_details"
                  control={control}
                  render={(item) => (
                    <POS
                      shipToNumber={stNumber}
                      billToNumber={btNumber}
                      countryCode={customer.customer_country || ''}
                      action={props.action}
                      posDetails={item.field.value || {} as POSDetails}
                      validationErrors={{}}
                      onChange={(posDetails) => {
                        item.field.onChange(posDetails);
                      }}
                    />
                  )}
                />
              ) : null)}
            </Grid>
          </Grid>
        </Panel>
      </Grid>
    );
  };

  const renderMarketingNotifications = () => {
    const headquartersFlag = watch('headquarters_flag');

    return (
      <Panel
        title={t('Marketing Notifications')}
        subtitle={headquartersFlag ? <></>
          : (
            <span style={{ marginLeft: 10 }}>
              <InformationTooltip tooltipVariant="light" title={t('Marketing Notifications can only be edited at Headquarters level')} />
            </span>
          )}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} spacing={2}>
            <Controller
              name="no_marketing_material"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Switch
                  checked={value}
                  onChange={(e) => {
                    onChange(e.target.checked);
                  }}
                  color="primary"
                  uncheckedLabel={t('No')}
                  checkedLabel={t('Yes')}
                  disabled={readOnly || !headquartersFlag}
                  label={t('Stop receiving marketing material')}
                  fullWidth={false}
                />
              )}
            />
          </Grid>
        </Grid>
      </Panel>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        <GenericActionButtons
          isDirty={formState.isDirty && googlePlacesStaus !== 'loading'}
          isSaving={mutateCommercial.isLoading || mutateGeoCommercial.isLoading}
          onSave={!formState.isDirty ? () => {} : handleSubmit(save, onError)}
          validateAction="location_information.update"
          readOnly={readOnly && !mutateGeoCommercial.isLoading}
        />
        <Grid item xs={12} md={12}>
          {renderPhysicalAddresInformation()}
        </Grid>
        <Grid item xs={12} md={12}>
          {renderServiceAddressInformation()}
        </Grid>
        <Grid item xs={12} md={12}>
          {renderHoursOfOperations()}
        </Grid>
        <Grid item xs={12} md={7}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              {renderMailingAddressInformation()}
            </Grid>
            <Grid item xs={12} md={12}>
              {renderMichelinIntegrations()}
            </Grid>
            {renderPosDetails()}
            <Grid item xs={12} md={12}>
              {renderMarketingNotifications()}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={5}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              {renderContactInformation()}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <PreventTransitionPrompt
        when={formState.isDirty}
      />
    </>
  );
}

export default CommercialLocationPanel;
